import { createContext, useEffect, useState } from "react";

export const CartContext = createContext({
  items: [],
  getProductQuantity: () => {},
  addOneToCart: () => {},
  removeOneFromCart: () => {},
  deleteFromCart: () => {},
  getTotalCost: () => {},
});

export function CartProvider({ children }) {
  const [cartProducts, setCartProducts] = useState([]);

  // Load cart from local storage on initial render
  useEffect(() => {
    const storedCart = JSON.parse(localStorage.getItem("cart"));
    if (storedCart) {
      setCartProducts(storedCart);
    }
  }, []);

  // Save cart to local storage whenever cartProducts updates
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cartProducts));
  }, [cartProducts]);

  function getProductQuantity(id) {
    return cartProducts.find((product) => product.id === id)?.quantity || 0;
  }

  function addOneToCart(id, productDetails, dataType) {
    setCartProducts((prevCart) => {
      const existingProduct = prevCart.find((product) => product.id === id);

      if (existingProduct) {
        return prevCart.map((product) =>
          product.id === id
            ? {
                ...product,
                quantity: product.quantity + 1,
                size: productDetails.selectedSize, // Always update size
                canvasType: productDetails.canvasType, // Always update canvasType
              }
            : product
        );
      } else {
        return [
          ...prevCart,
          {
            id,
            quantity: 1,
            dataType,
            ...productDetails, // Ensure size & canvasType are included
          },
        ];
      }
    });
  }

  function removeOneFromCart(id) {
    setCartProducts((prevCart) =>
      prevCart
        .map((product) =>
          product.id === id
            ? { ...product, quantity: product.quantity - 1 }
            : product
        )
        .filter((product) => product.quantity > 0)
    );
  }

  function deleteFromCart(id) {
    setCartProducts((prevCart) =>
      prevCart.filter((product) => product.id !== id)
    );
  }

  function getTotalCost() {
    return cartProducts.reduce(
      (total, cartItem) => total + cartItem.price * cartItem.quantity,
      0
    );
  }

  return (
    <CartContext.Provider
      value={{
        items: cartProducts,
        getProductQuantity,
        addOneToCart,
        removeOneFromCart,
        deleteFromCart,
        getTotalCost,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export default CartProvider;
